import { useCallback, useEffect, useMemo, useState } from "react";
import { Box, IconButton, Typography } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import { Button, CreateButton, Title, TopToolbar, useGetList } from "react-admin";
import { checkPermission } from "server/security/utils";
import permissions from "app/permissions";
import { getPermissions } from "auth/security/securityUtils";
import dataProvider from "server/dataProvider";
import { dormitoryRequests } from "api/dormitoryApi";
import { useNotifyError } from "utils/useNotifyError";
import { BUILDINGS, DormitoryBuildingStatuses, DormitoryServicePath } from "utils/constants";
import Combobox from "./Combobox";
import { compareBuildingNumbers } from "../../Partials/compareBuildingNumbers";
import RoomTile from "./RoomTile";
import { normalizeMapRoom, ROOM_STATE_PRESENTATION, splitCorridorRows } from "./roomHelpers";

const validateFloor = q => (/\D/.test(q) ? "Етажът трябва да е число." : null);

const FloorMap = ({ onShowTable }) => {
  const notifyError = useNotifyError();
  const canManage = checkPermission(getPermissions(), permissions.canManageDormitoryRooms);
  const {
    data: fetchedBuildings = [],
    isLoading: buildingsLoading,
    error: buildingsLoadError,
  } = useGetList(`${DormitoryServicePath}${BUILDINGS}`, {
    pagination: { page: 1, perPage: 500 },
    sort: { field: "id", order: "DESC" },
    filter: { status: DormitoryBuildingStatuses.Active },
  });

  const buildings = useMemo(
    () => [...fetchedBuildings].sort(compareBuildingNumbers),
    [fetchedBuildings],
  );

  const [rooms, setRooms] = useState([]);
  const [roomsLoaded, setRoomsLoaded] = useState(false);
  const [roomsError, setRoomsError] = useState(false);
  const [buildingIndex, setBuildingIndex] = useState(0);
  const [floorIndex, setFloorIndex] = useState(0);

  useEffect(() => {
    if (buildings.length === 0) {
      return;
    }
    if (buildingIndex >= buildings.length) {
      setBuildingIndex(0);
    }
  }, [buildings, buildingIndex]);

  const building = buildings[buildingIndex] || null;

  const fetchRooms = useCallback(
    async (buildingId, cancelled) => {
      setRoomsLoaded(false);
      setRoomsError(false);
      setFloorIndex(0);
      setRooms([]);

      try {
        const { data } = await dataProvider.customRequest(dormitoryRequests.getBuildingRooms(buildingId), null, "GET");
        if (cancelled.current) {
          return;
        }
        setRooms(Array.isArray(data) ? data.map(normalizeMapRoom) : []);
      } catch (err) {
        if (cancelled.current) {
          return;
        }
        notifyError(err);
        setRoomsError(true);
      } finally {
        if (!cancelled.current) {
          setRoomsLoaded(true);
        }
      }
    },
    [notifyError],
  );

  useEffect(() => {
    if (!building?.id) {
      return;
    }

    const cancelled = { current: false };
    fetchRooms(building.id, cancelled);
    return () => {
      cancelled.current = true;
    };
  }, [building, fetchRooms]);

  const floors = useMemo(() => [...new Set(rooms.map(room => room.floor))].sort((a, b) => a - b), [rooms]);

  useEffect(() => {
    if (floors.length === 0) {
      setFloorIndex(0);
      return;
    }
    if (floorIndex >= floors.length) {
      setFloorIndex(0);
    }
  }, [floors, floorIndex]);

  const floor = floors[floorIndex] ?? null;
  const floorRooms = useMemo(() => (floor == null ? [] : rooms.filter(room => room.floor === floor)), [rooms, floor]);
  const { top, bottom } = useMemo(() => splitCorridorRows(floorRooms), [floorRooms]);

  const toolbar = (
    <TopToolbar>
      <Button
        label="Покажи таблица"
        onClick={onShowTable}
      />
      {canManage ? <CreateButton /> : null}
    </TopToolbar>
  );

  if (buildingsLoading) {
    return (
      <>
        <Title title="Стаи" />
        {toolbar}
        <Typography>Зареждане ...</Typography>
      </>
    );
  }

  if (buildingsLoadError) {
    return (
      <>
        <Title title="Стаи" />
        {toolbar}
        <Typography>Картата не можа да се зареди. Опитайте отново.</Typography>
      </>
    );
  }

  if (buildings.length === 0) {
    return (
      <>
        <Title title="Стаи" />
        {toolbar}
        <Typography>Все още няма налични блокове.</Typography>
      </>
    );
  }

  return (
    <>
      <Title title="Стаи" />
      {toolbar}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
          <Typography
            variant="caption"
            color="text.secondary"
          >
            Блок
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IconButton
              aria-label="Предишен блок"
              disabled={buildingIndex <= 0}
              onClick={() => setBuildingIndex(i => Math.max(0, i - 1))}
            >
              <ArrowBackIcon />
            </IconButton>
            <Combobox
              options={buildings.map((b, i) => ({
                label: `${b.residentialNumber ?? b.name}`,
                value: i,
              }))}
              value={buildingIndex}
              onChange={setBuildingIndex}
              placeholder="Блок"
            />
            <IconButton
              aria-label="Следващ блок"
              disabled={buildingIndex >= buildings.length - 1}
              onClick={() => setBuildingIndex(i => Math.min(buildings.length - 1, i + 1))}
            >
              <ArrowForwardIcon />
            </IconButton>
          </Box>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
          <Typography
            variant="caption"
            color="text.secondary"
          >
            Етаж
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IconButton
              aria-label="По-долен етаж"
              disabled={floorIndex <= 0 || floors.length === 0}
              onClick={() => setFloorIndex(i => Math.max(0, i - 1))}
            >
              <ArrowDownwardIcon />
            </IconButton>
            <Combobox
              options={floors.map((f, i) => ({ label: `${f}`, value: i }))}
              value={floorIndex}
              onChange={setFloorIndex}
              placeholder="Етаж"
              disabled={floors.length === 0}
              validate={validateFloor}
            />
            <IconButton
              aria-label="По-горен етаж"
              disabled={floorIndex >= floors.length - 1 || floors.length === 0}
              onClick={() => setFloorIndex(i => Math.min(floors.length - 1, i + 1))}
            >
              <ArrowUpwardIcon />
            </IconButton>
          </Box>
        </Box>
      </Box>

      {!roomsLoaded ? (
        <Typography>Зареждане ...</Typography>
      ) : roomsError ? (
        <Typography>Картата не можа да се зареди. Опитайте отново.</Typography>
      ) : floors.length === 0 ? (
        <Typography>В този блок няма етажи с активни стаи.</Typography>
      ) : floorRooms.length === 0 ? (
        <Typography>Няма стаи на този етаж.</Typography>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          <Box sx={{ display: "flex", gap: 1, overflowX: "auto", pb: 0.5 }}>
            {top.map(room => (
              <RoomTile
                key={room.id}
                room={room}
              />
            ))}
          </Box>
          <Box
            sx={{
              bgcolor: "action.hover",
              px: 2,
              py: 1,
              borderRadius: 1,
              textAlign: "center",
            }}
          >
            <Typography
              variant="body2"
              color="text.secondary"
            >
              Коридор
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1, overflowX: "auto", pb: 0.5 }}>
            {bottom.map(room => (
              <RoomTile
                key={room.id}
                room={room}
              />
            ))}
          </Box>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, pt: 2 }}>
            {Object.entries(ROOM_STATE_PRESENTATION).map(([state, presentation]) => (
              <Box key={state} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: 0.5,
                    bgcolor: presentation.color,
                    opacity: 0.85,
                  }}
                />
                <Typography
                  variant="body2"
                  color="text.secondary"
                >
                  {presentation.label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </>
  );
};

export default FloorMap;
